import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ShieldCheck, Lock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { ArticleRenderer } from "@/components/article-renderer";

interface RequiredArticle {
  slug: string;
  title: string;
  summary: string | null;
  bodyMarkdown: string | null;
  orderIndex: number | null;
  acknowledgedAt: string | null;
}

interface MyStatusResponse {
  enforced: boolean;
  articlesRequired: RequiredArticle[];
  allAcknowledged: boolean;
}

const STATUS_QUERY_KEY = ["/api/security-training/my-status"];

export function SecurityTrainingGate() {
  const { toast } = useToast();
  const [activeIndex, setActiveIndex] = useState(0);
  const [confirmed, setConfirmed] = useState(false);

  const { data, isLoading, isError } = useQuery<MyStatusResponse>({
    queryKey: STATUS_QUERY_KEY,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  // Articles that still need to be acknowledged (unacked first, in order).
  const unacked = useMemo(() => {
    if (!data?.articlesRequired) return [];
    return [...data.articlesRequired]
      .filter(a => !a.acknowledgedAt)
      .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  }, [data]);

  const current = unacked[activeIndex];
  const totalRequired = data?.articlesRequired.length || 0;
  const completedCount = totalRequired - unacked.length;
  const percent = totalRequired === 0 ? 100 : Math.round((completedCount / totalRequired) * 100);

  // Reset the confirm checkbox whenever we move to a new article.
  useEffect(() => {
    setConfirmed(false);
  }, [current?.slug]);

  const ackMutation = useMutation({
    mutationFn: async (articleSlug: string) => {
      const res = await apiRequest("POST", "/api/security-training/acknowledge", { articleSlug });
      return res.json();
    },
    onSuccess: () => {
      // Refetch status; the gate will close once everything is acknowledged.
      queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
      // If there's another article in this batch, advance immediately so the UI feels responsive
      // before the refetch lands.
      if (activeIndex + 1 < unacked.length) {
        setActiveIndex(activeIndex + 1);
      } else {
        // Last one — reset index so the next render with fresh data starts fresh.
        setActiveIndex(0);
      }
    },
    onError: (err: Error) => {
      toast({
        title: "Could not record your acknowledgment",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  // Don't render anything when:
  // - still loading the initial status (don't flash the gate)
  // - the status query failed (treat as no enforcement; we'd rather under-block than break the app)
  // - enforcement is off OR everything is already acknowledged
  if (isLoading || isError) return null;
  if (!data?.enforced) return null;
  if (data.allAcknowledged) return null;
  if (!current) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="security-training-gate-title"
      className="fixed inset-0 z-[100] flex items-stretch justify-center bg-background/95 backdrop-blur-sm"
      data-testid="security-training-gate"
    >
      <div className="flex flex-col w-full h-full max-w-5xl mx-auto bg-background border-x border-border">
        {/* Header */}
        <div className="flex flex-col gap-3 px-6 py-5 border-b border-border bg-amber-50 dark:bg-amber-950/40">
          <div className="flex items-start gap-3">
            <Lock className="h-6 w-6 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h2
                id="security-training-gate-title"
                className="text-lg font-semibold text-amber-900 dark:text-amber-100"
              >
                Security training required
              </h2>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                Your workspace administrator has made these articles mandatory. Read each one and
                confirm you understand it before continuing into the app.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Progress value={percent} className="h-2 flex-1" data-testid="progress-training" />
            <span
              className="text-xs font-medium text-amber-900 dark:text-amber-100 tabular-nums"
              data-testid="text-training-progress"
            >
              {completedCount} of {totalRequired} complete
            </span>
          </div>
        </div>

        {/* Article body */}
        <div className="flex-1 overflow-auto px-6 py-6">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-2">
              <ShieldCheck className="h-3 w-3" />
              <span>Required reading</span>
              <span aria-hidden="true">·</span>
              <span data-testid="text-current-position">
                Article {activeIndex + 1} of {unacked.length}
              </span>
            </div>
            <h3 className="text-2xl font-bold mb-2" data-testid={`heading-article-${current.slug}`}>
              {current.title}
            </h3>
            {current.summary && (
              <p className="text-muted-foreground mb-6">{current.summary}</p>
            )}

            {current.bodyMarkdown ? (
              <ArticleRenderer
                markdown={current.bodyMarkdown}
                showToc={false}
                className="!flex-col"
              />
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground py-12">
                <AlertTriangle className="h-4 w-4" />
                <span>This article has no content yet — ask your admin to publish it.</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer / acknowledgment */}
        <div className="border-t border-border bg-card px-6 py-4">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <label
              className="flex items-start sm:items-center gap-3 cursor-pointer select-none"
              data-testid="label-confirm-read"
            >
              <Checkbox
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(v === true)}
                data-testid="checkbox-confirm-read"
              />
              <span className="text-sm">
                I have read and understood <span className="font-medium">{current.title}</span>.
              </span>
            </label>

            <Button
              size="lg"
              disabled={!confirmed || ackMutation.isPending || !current.bodyMarkdown}
              onClick={() => ackMutation.mutate(current.slug)}
              data-testid="button-acknowledge-article"
              className="min-w-[180px]"
            >
              {ackMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Recording…
                </>
              ) : activeIndex + 1 < unacked.length ? (
                <>
                  Acknowledge & continue
                  <CheckCircle2 className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Acknowledge & enter app
                  <CheckCircle2 className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
